import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Flame, Plus, ShoppingBag, X, Heart } from 'lucide-react';
import { useRestaurantData } from '../hooks/useRestaurantData';
import { useCart } from '../context/CartContext';
import { useFavorites } from '../context/FavoritesContext';
import { CustomizationModal, UpSellModal } from './Modals';
import './Menu.css';

const Menu = () => {
    const data = useRestaurantData();
    const { addToCart } = useCart();
    const { toggleFavorite, isFavorite } = useFavorites();
    const [activeCategory, setActiveCategory] = useState(data.menu[0]?.category);

    // Modals state
    const [customizingItem, setCustomizingItem] = useState(null);
    const [showUpSell, setShowUpSell] = useState(false);

    if (!data || !data.menu || data.menu.length === 0) return null;

    const handleAddToCart = (item) => {
        if (item.customizable) {
            setCustomizingItem(item);
        } else {
            addToCart(item);
            setShowUpSell(true);
        }
    };

    return (
        <section id="menu" className="menu-section">
            <div className="container">
                <div className="menu-header">
                    <h2 className="menu-title">Notre Carte</h2>
                    <p className="menu-subtitle">Découvrez nos créations artisanales préparées avec passion et des produits de qualité.</p>
                </div>

                <div className="category-filters">
                    {data.menu.map((cat) => (
                        <button
                            key={cat.category}
                            onClick={() => setActiveCategory(cat.category)}
                            className={`category-btn ${activeCategory === cat.category ? 'category-btn-active' : 'category-btn-inactive'}`}
                        >
                            {cat.category}
                        </button>
                    ))}
                </div>

                <div className="menu-grid">
                    {data.menu
                        .find((cat) => cat.category === activeCategory)
                        ?.items.map((item, idx) => (
                            <motion.div
                                key={item.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.1 }}
                                className="menu-card"
                            >
                                <div className="item-info">
                                    <div className="item-header">
                                        <div className="flex flex-col">
                                            <h3 className="item-name">{item.name}</h3>
                                            <span className="item-price">{item.price.toFixed(2)}€</span>
                                        </div>
                                        <button
                                            className={`fav-toggle-btn ${isFavorite(item.id) ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleFavorite(item);
                                            }}
                                        >
                                            <Heart size={20} fill={isFavorite(item.id) ? "currentColor" : "none"} />
                                        </button>
                                    </div>
                                    <p className="item-desc">{item.description}</p>

                                    <div className="item-tags">
                                        {item.popular && (
                                            <span className="tag tag-popular">
                                                <Flame size={10} /> Populaire
                                            </span>
                                        )}
                                        <span className="tag tag-homemade">
                                            Fait Maison
                                        </span>
                                        {item.customizable && (
                                            <span className="tag bg-blue-50 text-blue-500 border border-blue-100 uppercase text-[10px] font-black px-2 py-1 rounded-full">
                                                Personnalisable
                                            </span>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => handleAddToCart(item)}
                                        className={`add-to-cart-btn ${item.outOfStock ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}
                                        disabled={item.outOfStock}
                                    >
                                        {item.outOfStock ? 'Épuisé' : <><Plus size={16} /> Ajouter au panier</>}
                                    </button>
                                </div>

                                <div className="hover-icon">
                                    {item.outOfStock ? <X size={16} /> : <ShoppingBag size={16} />}
                                </div>

                                {item.outOfStock && (
                                    <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px] flex items-center justify-center pointer-events-none">
                                        <span className="bg-dark text-white px-4 py-2 rounded-full font-black text-xs uppercase tracking-widest rotate-[-10deg] shadow-xl">
                                            Bientôt de retour
                                        </span>
                                    </div>
                                )}
                            </motion.div>
                        ))}
                </div>
            </div>

            {/* Premium Modals */}
            <CustomizationModal
                item={customizingItem}
                isOpen={!!customizingItem}
                onClose={() => {
                    setCustomizingItem(null);
                    setShowUpSell(true);
                }}
            />

            <UpSellModal
                isOpen={showUpSell}
                onClose={() => setShowUpSell(false)}
            />
        </section>
    );
};

export default Menu;
